import {
  BlueprintExceptionRead,
  PolicyExceptionApiAddExceptionRequest,
  PolicyExceptionApiRemoveExceptionRequest,
} from '@ariksa/compliance-policies/api';
import {
  ContextApiGetRiskContextResRequest,
  RiskContextResponse,
} from '@ariksa/inventory-core/api';
import {
  AggregatedAlertsPaginatedResponse,
  AlertPaginatedResponse,
  AlertsApiAlertReportRequest,
  AlertsApiGetAggregatedAlertsRequest,
  AlertsApiGetAlertsRequest,
  ClientResponse,
  ClientsApiCreateTicketRequest,
  ClientsApiGetClientDetailsRequest,
  ClientsApiGetClientTicketFieldsRequest,
  ClientsApiGetTicketFieldsRequest,
  ClientsApiNotifyClientRequest,
  SnoozedAlertsApiAddSnoozedAlertRequest,
  SnoozedAlertsApiStopSnoozeRequest,
  TicketFieldsResponse,
} from '@ariksa/notification/api';
import { call } from 'redux-saga/effects';
import { QueryContext } from 'services/utils/QueryContext';
import { takeLatestAction } from 'services/utils/takeLatestAction';

import {
  AlertVaultAlertService,
  AlertVaultClientService,
  AlertVaultDeliveryService,
  isAlertVaultEnabled,
} from 'api/alert-vault/client';
import { AlertVaultJiraService } from 'api/alert-vault/jira';
import { alertVaultSort } from 'api/alert-vault/sort';
import {
  ComplianceService,
  InventoryService,
  NotificationService,
} from 'api/index';

import { withoutClientId } from './notificationUtils';
import { actions } from './slice';

export function* alertsSaga() {
  yield takeLatestAction(actions.getTicketFields, doGetTicketFields);
  yield takeLatestAction(actions.getTicketInfo, doGetTicketInfo);
  yield takeLatestAction(actions.createTicket, doCreateTicket);
  yield takeLatestAction(actions.createJiraTicket, doCreateJiraTicket);
  yield takeLatestAction(actions.createNotification, doCreateNotification);
  yield takeLatestAction(actions.addException, doAddException);
  yield takeLatestAction(actions.getClients, doGetClients);

  yield takeLatestAction(actions.stopSnooze, doStopSnooze);

  yield takeLatestAction(
    actions.getAggregatedFindings,
    doGetAggregatedFindings,
  );
  yield takeLatestAction(actions.getAlerts, doGetAlerts);
  yield takeLatestAction(actions.snoozeAlert, doSnoozeAlert);
  yield takeLatestAction(actions.removeException, doRemoveException);
  yield takeLatestAction(actions.downloadReport, doDownloadReport);
  yield takeLatestAction(
    actions.getIndividualRiskContext,
    doGetIndividualAlertRiskContext,
  );
}

export function* doGetIndividualAlertRiskContext(
  ctx: QueryContext<
    Record<string, RiskContextResponse[]>,
    ContextApiGetRiskContextResRequest
  >,
) {
  yield call(
    ctx.fetch,
    () => InventoryService.Context.getRiskContextRes(ctx.params),
    {
      errorMsg: 'Failed to get risk context!',
    },
  );
}

export function* doAddException(
  ctx: QueryContext<
    BlueprintExceptionRead,
    PolicyExceptionApiAddExceptionRequest
  >,
) {
  yield call(
    ctx.fetch,
    () => ComplianceService.PolicyException.addException(ctx.params),
    {
      errorMsg: 'Failed to add exception!',
      successMsg: 'Exception added successfully.',
    },
  );
}

function* doGetClients(
  ctx: QueryContext<ClientResponse[], ClientsApiGetClientDetailsRequest>,
) {
  yield call(
    ctx.fetch,
    () =>
      isAlertVaultEnabled()
        ? AlertVaultClientService.list(ctx.params.clientName)
        : NotificationService.Clients.getClientDetails(ctx.params),
    { errorMsg: 'Failed to get clients.' },
  );
}

function* doGetTicketInfo(
  ctx: QueryContext<any, ClientsApiGetTicketFieldsRequest>,
) {
  yield call(
    ctx.fetch,
    () => NotificationService.Clients.getTicketFields(ctx.params),
    { errorMsg: 'Failed to get ticket info.' },
  );
}
function* doGetTicketFields(
  ctx: QueryContext<
    TicketFieldsResponse,
    ClientsApiGetClientTicketFieldsRequest
  >,
) {
  yield call(
    ctx.fetch,
    () =>
      isAlertVaultEnabled()
        ? AlertVaultJiraService.ticketFields(ctx.params)
        : NotificationService.Clients.getClientTicketFields(ctx.params),
    { errorMsg: 'Failed to get ticket fields.' },
  );
}

function* doCreateTicket(
  ctx: QueryContext<any, ClientsApiNotifyClientRequest>,
) {
  yield call(
    ctx.fetch,
    () =>
      isAlertVaultEnabled() &&
      ctx.params?.notifyClients?.client_id &&
      ctx.params?.notifyClients?.alert_uuid
        ? AlertVaultDeliveryService.create({
            alert_id: ctx.params.notifyClients.alert_uuid,
            client_id: ctx.params.notifyClients.client_id,
            body: ctx.params.notifyClients.ticket_fields?.summary,
            metadata: ctx.params.notifyClients.ticket_fields,
          })
        : NotificationService.Clients.notifyClient(ctx.params),
    {},
  );
}

function* doCreateJiraTicket(
  ctx: QueryContext<any, ClientsApiCreateTicketRequest>,
) {
  yield call(
    ctx.fetch,
    () =>
      // Alert Vault creates the Jira issue itself, for alerts and findings.
      isAlertVaultEnabled()
        ? AlertVaultJiraService.createTicket(ctx.params)
        : NotificationService.Clients.createTicket(ctx.params),
    {},
  );
}

function* doCreateNotification(
  ctx: QueryContext<any, ClientsApiNotifyClientRequest>,
) {
  yield call(
    ctx.fetch,
    () =>
      isAlertVaultEnabled() &&
      ctx.params?.notifyClients?.client_id &&
      ctx.params?.notifyClients?.alert_uuid
        ? AlertVaultDeliveryService.create({
            alert_id: ctx.params.notifyClients.alert_uuid,
            client_id: ctx.params.notifyClients.client_id,
            body: ctx.params.notifyClients.message,
            metadata: ctx.params.notifyClients.ticket_fields,
          })
        : // client_id is only for Alert Vault; keep the legacy request unchanged.
          NotificationService.Clients.notifyClient(withoutClientId(ctx.params)),
    {},
  );
}

/*Get Aggregated Findings*/
export function* doGetAggregatedFindings(
  ctx: QueryContext<
    AggregatedAlertsPaginatedResponse,
    AlertsApiGetAggregatedAlertsRequest
  >,
) {
  yield call(
    ctx.fetch,
    () =>
      isAlertVaultEnabled()
        ? AlertVaultAlertService.aggregates({
            accountId: ctx.params.accountId,
            environmentId: ctx.params.environmentId,
            resourceType: ctx.params.resourceTypes?.[0],
            category:
              ctx.params.alertCategory ?? ctx.params.alertCategories?.[0],
            categoryClass: ctx.params.categoryClass,
            severity: ctx.params.severities,
            search: ctx.params.search,
            from: ctx.params.startDate,
            to: ctx.params.endDate,
            groupBy: 'resource',
            // The Issues page sends its page in the request params, not the
            // query state, so ctx.page would always be the 20-row default.
            pageNumber: ctx.params.pageNumber ?? ctx.page.page_number,
            pageSize: ctx.params.pageSize ?? ctx.page.page_size,
          })
        : NotificationService.Alerts.getAggregatedAlerts(ctx.params),
    { errorMsg: 'Failed to get aggregate findings!' },
  );
}

/*Get Alerts*/
function* doGetAlerts(
  ctx: QueryContext<AlertPaginatedResponse, AlertsApiGetAlertsRequest>,
) {
  yield call(
    ctx.fetch,
    () =>
      isAlertVaultEnabled()
        ? AlertVaultAlertService.list({
            accountId: ctx.params.accountId,
            environmentId: ctx.params.environmentId,
            category: ctx.params.alertCategories?.[0],
            resourceType: ctx.params.resourceTypes?.[0],
            policyId: ctx.params.alertRuleId?.[0],
            severity: ctx.params.severities,
            search: ctx.params.searchString,
            from: ctx.params.startDate,
            to: ctx.params.endDate,
            status: ctx.params.status,
            ...alertVaultSort(ctx.params.sortField, ctx.params.sortOrder),
            // Use the table's page from the request params (see above).
            pageNumber: ctx.params.pageNumber ?? ctx.page.page_number,
            pageSize: ctx.params.pageSize ?? ctx.page.page_size,
          })
        : NotificationService.Alerts.getAlerts(ctx.params),
    { errorMsg: 'Failed to get alerts' },
  );
}

function* doStopSnooze(
  ctx: QueryContext<any, SnoozedAlertsApiStopSnoozeRequest>,
) {
  yield call(
    ctx.fetch,
    () =>
      isAlertVaultEnabled()
        ? AlertVaultAlertService.lifecycle(ctx.params.alertId, 'unsnoozed')
        : NotificationService.SnoozedAlerts.stopSnooze(ctx.params),
    {
      errorMsg: 'Failed to remove snooze!',
      successMsg: 'Snooze removed successfully.',
    },
  );
}
function* doSnoozeAlert(
  ctx: QueryContext<any, SnoozedAlertsApiAddSnoozedAlertRequest>,
) {
  yield call(
    ctx.fetch,
    () =>
      isAlertVaultEnabled()
        ? AlertVaultAlertService.lifecycle(ctx.params.alertId, 'snoozed', {
            due_date: ctx.params.snoozeRequest?.snooze_duration,
          })
        : NotificationService.SnoozedAlerts.addSnoozedAlert(ctx.params),
    {
      errorMsg: 'Failed to snooze alert!',
      successMsg: 'Alert snoozed successfully.',
    },
  );
}

function* doRemoveException(
  ctx: QueryContext<any, PolicyExceptionApiRemoveExceptionRequest>,
) {
  yield call(ctx.fetch, () =>
    ComplianceService.PolicyException.removeException(ctx.params),
  );
}

export function* doDownloadReport(
  ctx: QueryContext<any, AlertsApiAlertReportRequest>,
) {
  yield call(ctx.fetch, () =>
    NotificationService.Alerts.alertReport(ctx.params),
  );
}
